/**
 * What has happened to each mark on the page.
 *
 * Recognition used to be a boolean the pad inferred twice, in two places, from
 * two different numbers: the notation layer decided a note was "unsure" below
 * 0.55, while the pad faded the learner's ink out for *every* recognised note
 * regardless. So a mark the app was openly unsure about still vanished behind a
 * tentative notehead, and the one instruction the brief is most explicit about —
 * keep uncertain ink exactly where it was drawn — was broken by the confident
 * path being the only path.
 *
 * The states are the ones the checkpoint names, and they are a real sequence:
 *
 *   drawing      the pen is down; nothing has been read yet
 *   pending      the stroke is finished and the recogniser has not answered
 *   recognized   read with enough confidence to engrave
 *   uncertain    read, but not well enough to take the learner's mark away
 *   rejected     not part of any reading — a stray line, a smudge
 *   committed    engraved, and the raw ink has faded behind the notation
 *
 * Only `recognized -> committed` is allowed to hide ink. Nothing here ever
 * deletes a stroke: `committed` means "no longer painted", and `showAllInk()`
 * brings it straight back.
 */

import type { RecognitionResult } from './recognize';
import type { Stroke } from './types';

export type SymbolState =
  | 'drawing'
  | 'pending'
  | 'recognized'
  | 'uncertain'
  | 'rejected'
  | 'committed';

/**
 * The line between "engrave it" and "leave it alone".
 *
 * One constant, used by the notation layer to colour a tentative note and by the
 * pad to decide whether the ink may fade. Two copies of this number is how the
 * two halves came to disagree.
 */
export const CONFIDENT_ENOUGH = 0.55;

export interface SymbolStateOptions {
  /** Strokes whose ink has already faded behind its notation. */
  committed?: Iterable<string>;
  /** The stroke currently under the pen, if any. */
  drawingId?: string | null;
  /** True while the debounce is running and no result describes these strokes yet. */
  awaitingResult?: boolean;
}

/**
 * Classify every stroke on the page.
 *
 * Takes the strokes rather than deriving the list from the result, because the
 * marks that matter most are the ones the result does *not* mention.
 */
export function symbolStates(
  strokes: Stroke[],
  result: RecognitionResult | null,
  options: SymbolStateOptions = {},
): Map<string, SymbolState> {
  const committed = new Set(options.committed ?? []);
  const states = new Map<string, SymbolState>();

  // Strokes claimed by a note, and how confident that note was.
  const noteConfidence = new Map<string, number>();
  for (const note of result?.notes ?? []) {
    for (const id of note.strokeIds) {
      noteConfidence.set(id, Math.max(noteConfidence.get(id) ?? 0, note.confidence));
    }
  }
  // A glyph that is not part of a note — an accidental waiting for one, say — is
  // still a reading, and its ink must not be treated as a stray mark.
  const glyphIds = new Set((result?.glyphs ?? []).flatMap((glyph) => glyph.strokeIds));

  for (const stroke of strokes) {
    if (stroke.id === options.drawingId) {
      states.set(stroke.id, 'drawing');
      continue;
    }
    if (!result || options.awaitingResult) {
      states.set(stroke.id, 'pending');
      continue;
    }
    const confidence = noteConfidence.get(stroke.id);
    if (confidence === undefined) {
      states.set(stroke.id, glyphIds.has(stroke.id) ? 'uncertain' : 'rejected');
      continue;
    }
    if (confidence < CONFIDENT_ENOUGH) {
      states.set(stroke.id, 'uncertain');
      continue;
    }
    states.set(stroke.id, committed.has(stroke.id) ? 'committed' : 'recognized');
  }

  return states;
}

/**
 * The strokes whose ink may fade behind its notation.
 *
 * The whole guard, in one place: a note the app is not sure about keeps its ink,
 * because a tentative notehead sitting where the learner's mark used to be is
 * the app asserting something it does not know.
 */
export function committableStrokeIds(result: RecognitionResult | null): string[] {
  if (!result) return [];
  return result.notes
    .filter((note) => note.confidence >= CONFIDENT_ENOUGH)
    .flatMap((note) => note.strokeIds);
}

/** True when a note is confident enough to engrave over the learner's mark. */
export function isConfident(confidence: number): boolean {
  return confidence >= CONFIDENT_ENOUGH;
}
